// Renders every open tab's page component into the DOM simultaneously.
// Only the active tab is visible (display:block); the others are hidden (display:none)
// so that their internal state (form fields, scroll position, filters, search text…)
// is preserved across tab switches.
//
// Dynamic tabs (opened via `openFormTab`) carry their own inline `render` function.
// Registered tabs look up `render` from TAB_REGISTRY.
//
// Sprint 2026-Jun — deep-link permission guard. Even if a restricted user
// types a URL directly (or a stale tab is restored from localStorage), a
// page they have no `view` permission for renders an "Access restricted"
// panel instead of the page. Backend endpoints enforce the same
// permissions server-side — this guard is UX defence-in-depth.
import { useTabs } from "@/contexts/TabsContext";
import { useAuth } from "@/contexts/AuthContext";
import { TAB_REGISTRY } from "@/lib/tabRegistry";
import { canSeePath } from "@/lib/permissions";
import { ShieldOff } from "lucide-react";

function RestrictedPanel({ path }) {
  return (
    <div data-testid="access-restricted-panel"
      className="flex flex-col items-center justify-center py-24 text-center">
      <ShieldOff size={40} className="text-slate-300 mb-4" />
      <div className="text-lg font-bold text-slate-700">Access restricted</div>
      <div className="text-sm text-slate-500 mt-1 max-w-md">
        Your role does not have permission to view <span className="font-mono">{path}</span>.
        Contact your administrator if you believe you should have access.
      </div>
    </div>
  );
}

export default function TabContent() {
  const { openTabs, activePath } = useTabs();
  const { user, hasPerm } = useAuth();

  // Data-dense pages that need every pixel — rendered full-width
  // instead of the default 1600px centered column.
  const WIDE_PATHS = new Set(["/purchase/post-payment"]);

  return (
    <div className="flex-1 min-h-0">
      {openTabs.map((tab) => {
        const renderFn = tab.dynamic ? tab.render : TAB_REGISTRY[tab.path]?.render;
        if (typeof renderFn !== "function") return null;
        const isActive = tab.path === activePath;
        const allowed = tab.dynamic || canSeePath(tab.path, hasPerm, user);
        const wide = WIDE_PATHS.has(tab.path);
        return (
          <div
            key={tab.path}
            data-testid={`tab-content-${tab.path.replace(/\//g, "-")}`}
            style={{ display: isActive ? "block" : "none" }}
            className={wide
              ? "px-3 sm:px-5 lg:px-6 py-6 w-full"
              : "px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-[1600px] mx-auto"}
          >
            {allowed ? renderFn() : <RestrictedPanel path={tab.path} />}
          </div>
        );
      })}
    </div>
  );
}
